import { canonicalBehaviorPattern } from "../constants/behaviorPatterns.js";
import {
  GAD7_ITEMS,
  GAD7_MAX_SCORE,
  GAD7_WINDOW_DAYS,
  GAD7_FREQUENCY_LABELS,
  getGad7FrequencyLabel,
  getGad7SeverityLabel,
  getGad7SeverityNote
} from "../constants/gad7.js";
import { getInsightCombinedText, getInsightEmotion } from "./mentalPatternAnalysis.js";
import { getReportingThinkingPattern } from "./confirmedEmotion.js";

const STRONG_HIT = 2;
const LIGHT_HIT = 1;

/**
 * GAD item signals use confirmed/corrected emotions (via getInsightEmotion),
 * confirmed CBT patterns (Quick Clarity) or legacy behavior_pattern,
 * and reflection text including Quick Clarity fields.
 */
const ITEM_SIGNALS = {
  nervous_anxious: {
    emotions: new Set(["Anxious", "Fearful", "Nervous", "Worried", "Panicked"]),
    regex: [/\b(nervous|anxious|anxiety|on edge|uneasy|jittery|keyed up|tense|tension)\b/i],
    cbtPatterns: new Set(["catastrophizing", "fortune_telling", "emotional_reasoning"])
  },
  cant_stop_worrying: {
    emotions: new Set(["Anxious", "Skeptical", "Confused", "Worried", "Overwhelmed"]),
    regex: [
      /\b(can't stop (?:think|worry)|cannot stop (?:think|worry)|can't control (?:my )?worry|uncontrollable worry)\b/i,
      /\b(ruminat|overthink|what if|spiral|looping|keeps? running through|replay(?:ing)?)\b/i
    ],
    behaviorPatterns: new Set(["Uncertainty"]),
    cbtPatterns: new Set(["catastrophizing", "fortune_telling", "overgeneralization", "mental_filtering"])
  },
  worry_many_things: {
    emotions: new Set(["Anxious", "Overwhelmed", "Skeptical", "Worried"]),
    regex: [
      /\b(worry(?:ing)? about (?:many|different|everything|all)|too many worries|multiple worries)\b/i,
      /\b(everything (?:could|might)|all the things that could)\b/i
    ],
    behaviorPatterns: new Set(["Uncertainty", "Complexity"]),
    cbtPatterns: new Set(["catastrophizing", "overgeneralization", "all_or_nothing"])
  },
  trouble_relaxing: {
    emotions: new Set(["Overwhelmed", "Anxious", "Frustrated", "Stressed", "Tense"]),
    regex: [
      /\b(can't relax|cannot relax|can't unwind|trouble relax|unable to relax|can't rest)\b/i,
      /\b(stress(?:ed|ful)?|overwhelm(?:ed|ing)?|tense|tension|frazzled|wound up)\b/i
    ],
    cbtPatterns: new Set(["should_statements", "mental_filtering"])
  },
  restless: {
    emotions: new Set(["Anxious", "Frustrated", "Energetic"]),
    regex: [
      /\b(can't sit still|restless|restlessness|kept moving|always doing|stay(?:ing)? busy)\b/i,
      /\b(restless productivity|no time to (?:feel|think)|distract(?:ed|ing)? myself)\b/i
    ],
    cbtPatterns: new Set(["should_statements"])
  },
  irritable: {
    emotions: new Set(["Frustrated", "Angry", "Hostile", "Irritated", "Annoyed", "Rage", "Furious"]),
    regex: [
      /\b(irritab|snappy|short fuse|on edge|snapped at|easily annoyed|cranky|grumpy)\b/i,
      /\b(lash(?:ed)? out|quick to anger|little things (?:set|make) me)\b/i
    ],
    cbtPatterns: new Set(["should_statements", "labeling", "all_or_nothing"])
  },
  afraid_awful: {
    emotions: new Set(["Fearful", "Anxious", "Insecure", "Panicked", "Dread"]),
    regex: [
      /\b(something awful|worst case|catastroph|disaster|doom|dread|terrified|panic)\b/i,
      /\b(afraid (?:that|something)|fear (?:that|something)|might happen)\b/i
    ],
    behaviorPatterns: new Set(["Insecurity"]),
    cbtPatterns: new Set(["catastrophizing", "fortune_telling", "mind_reading"])
  }
};

function parseInsightDate(insight) {
  const raw = insight.created_at != null ? insight.created_at : insight.createdAt;
  const d = raw ? new Date(raw) : new Date();
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

function insightsInWindow(insights, maxDays = GAD7_WINDOW_DAYS) {
  const now = Date.now();
  const msDay = 86400000;
  return (insights || []).filter((item) => {
    const age = (now - parseInsightDate(item).getTime()) / msDay;
    return age >= 0 && age < maxDays;
  });
}

function scoreInsightForItem(insight, itemId) {
  const config = ITEM_SIGNALS[itemId];
  if (!config) return 0;

  let strength = 0;
  const emotion = getInsightEmotion(insight);
  if (emotion && config.emotions?.has(emotion)) strength += STRONG_HIT;

  const reportedPattern = getReportingThinkingPattern(insight);
  if (reportedPattern?.kind === "legacy" && config.behaviorPatterns?.has(reportedPattern.key)) {
    strength += STRONG_HIT;
  }
  if (reportedPattern?.kind === "cbt" && config.cbtPatterns?.has(reportedPattern.key)) {
    strength += STRONG_HIT;
  }

  // Legacy rows may still expose behavior_pattern without going through confirmation helper.
  if (!reportedPattern) {
    const legacy = canonicalBehaviorPattern(insight?.insight_json?.behavior_pattern);
    if (legacy && config.behaviorPatterns?.has(legacy)) strength += STRONG_HIT;
  }

  const text = getInsightCombinedText(insight);
  for (const re of config.regex || []) {
    if (re.test(text)) {
      strength += LIGHT_HIT;
      break;
    }
  }

  return strength > 0 ? Math.min(STRONG_HIT, strength) : 0;
}

function hitsToItemScore(hits, weightedHits) {
  if (!hits || hits === 0) return 0;
  if (hits >= 5 || weightedHits >= 8) return 3;
  if (hits >= 3 || weightedHits >= 4) return 2;
  return 1;
}

export function scoreGad7Item(insight, itemId) {
  return scoreInsightForItem(insight, itemId);
}

export function isGad7RelevantInsight(insight) {
  return GAD7_ITEMS.some((item) => scoreInsightForItem(insight, item.id) > 0);
}

export function analyzeGad7(insights) {
  const windowInsights = insightsInWindow(insights);
  const itemStats = GAD7_ITEMS.map((item) => ({
    ...item,
    hits: 0,
    weightedHits: 0,
    score: 0,
    frequencyLabel: GAD7_FREQUENCY_LABELS[0]
  }));

  const matchingInsights = [];

  for (const insight of windowInsights) {
    let insightHit = false;
    for (const stat of itemStats) {
      const hitStrength = scoreInsightForItem(insight, stat.id);
      if (hitStrength > 0) {
        stat.hits += 1;
        stat.weightedHits += hitStrength;
        insightHit = true;
      }
    }
    if (insightHit) {
      matchingInsights.push({
        id: insight.id,
        date: parseInsightDate(insight).toISOString(),
        emotion: getInsightEmotion(insight) || null,
        preview: (insight.entry_text || "").slice(0, 120)
      });
    }
  }

  let totalScore = 0;
  for (const stat of itemStats) {
    stat.score = hitsToItemScore(stat.hits, stat.weightedHits);
    stat.frequencyLabel = getGad7FrequencyLabel(stat.score);
    totalScore += stat.score;
  }

  totalScore = Math.min(GAD7_MAX_SCORE, totalScore);
  matchingInsights.sort((a, b) => new Date(b.date) - new Date(a.date));

  const hasData = matchingInsights.length > 0;

  return {
    items: itemStats,
    totalScore: hasData ? totalScore : null,
    severityLabel: hasData ? getGad7SeverityLabel(totalScore) : null,
    severityNote: hasData ? getGad7SeverityNote(totalScore) : null,
    matchingInsights,
    reflectionsInWindow: windowInsights.length,
    hasData
  };
}
